"use client";

import React from 'react';
import Link from 'next/link';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { FaCoffee, FaLeaf, FaShoppingBag, FaArrowRight, FaProjectDiagram } from 'react-icons/fa';
import { FaHeartPulse } from 'react-icons/fa6';

// Default icons for projects without images
const DEFAULT_ICONS = [
  FaCoffee,
  FaLeaf,
  FaHeartPulse,
  FaShoppingBag,
  FaProjectDiagram,
];

const Portfolio = () => {
  const projects = useQuery(api.projects.queries.listPortfolioProjects);

  // Show loading state
  if (projects === undefined) {
    return (
      <div id="projects-page" className="page-section active block opacity-100">
        <section className="relative pt-32 pb-20 px-6 bg-[#030305]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-kuttos-cyan font-tech text-sm tracking-[0.2em] uppercase mb-4">Our Portfolio</h2>
              <h3 className="font-display text-4xl md:text-5xl font-bold text-white">Proven Solutions</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="glass-panel rounded-2xl overflow-hidden h-80 animate-pulse">
                  <div className="w-full h-full bg-gray-900"></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Show empty state
  if (!projects || projects.length === 0) {
    return (
      <div id="projects-page" className="page-section active block opacity-100">
        <section className="relative pt-32 pb-20 px-6 bg-[#030305]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-kuttos-cyan font-tech text-sm tracking-[0.2em] uppercase mb-4">Our Portfolio</h2>
              <h3 className="font-display text-4xl md:text-5xl font-bold text-white">Proven Solutions</h3>
            </div>
            <div className="text-center text-gray-400">
              <p>No projects available at the moment.</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div id="projects-page" className="page-section active block opacity-100">
      <section className="relative pt-32 pb-20 px-6 bg-[#030305]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-kuttos-cyan font-tech text-sm tracking-[0.2em] uppercase mb-4">Our Portfolio</h2>
            <h3 className="font-display text-4xl md:text-5xl font-bold text-white">Proven Solutions</h3>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project: any, index: number) => {
              // Get default icon for projects without images
              const DefaultIcon = DEFAULT_ICONS[index % DEFAULT_ICONS.length];
              
              return (
                <Link
                  key={project._id}
                  href={`/portfolio/${project.slug}`}
                  className="group block relative glass-panel rounded-2xl overflow-hidden hover:border-kuttos-cyan/50 transition-all h-80"
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent z-10"></div>
                  
                  {project.imageUrl ? (
                    <div className="w-full h-full relative group-hover:scale-105 transition-transform duration-700">
                      <img
                        src={project.imageUrl}
                        alt={project.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gray-900 group-hover:scale-105 transition-transform duration-700 flex items-center justify-center text-gray-700">
                      <DefaultIcon className="text-6xl opacity-20" />
                    </div>
                  )}
                  
                  <div className="absolute bottom-0 left-0 p-8 z-20 w-full">
                    <div className="flex justify-between items-end">
                      <div>
                        <h4 className="text-2xl font-bold text-white mb-2">{project.name}</h4>
                        <p className="text-gray-400 text-sm line-clamp-2">{project.description}</p>
                      </div>
                      <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-kuttos-cyan group-hover:text-black transition-all">
                        <FaArrowRight />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;